<template>
  <v-card flat class="futuristic-card">
    <v-card-title class="card-header">
      <div>
        <h4 class="earth-color">CHECK IN TODAY</h4>
        <div class="title-decoration"></div>
      </div>
      <v-spacer></v-spacer>
      <v-text-field append-icon="search"
                    label="Search"
                    single-line
                    hide-details
                    v-model="search"
                    class="futuristic-search"></v-text-field>
    </v-card-title>

    <!-- Booking Type Tabs with "All" as default -->
    <v-tabs v-model="selectedTypeTab" slider-color="primary" class="type-tabs" v-if="bookingTypes.length > 0">
      <v-tab v-for="(type, index) in bookingTypes" :key="'type-' + index" ripple>
        {{ type.label }} ({{ getTypeCount(type.value) }})
      </v-tab>
    </v-tabs>

    <v-data-table :headers="columns"
                  :items="filteredItems"
                  :search="search"
                  :rows-per-page-items="[10, 25, { text: 'All', value: -1 }]"
                  :disable-initial-sort="true"
                  :loading="$loadingTrue"
                  class="futuristic-table">
      <v-progress-linear slot="progress" color="#587e27" indeterminate></v-progress-linear>
      <template slot="items" slot-scope="props">
        <td data-label="Ref">
          <router-link :to="{ name: 'Booking', params: { booking_id: props.item.booking_public_id }}">
            <a class="futuristic-link"><u>{{ props.item.booking_ref_code }}</u></a>
          </router-link>
        </td>
        <td data-label="Vehicle Registration">{{ props.item.vehicle_registration }}</td>
        <td data-label="Booking Type">{{ props.item.booking_type }}</td>
        <td data-label="Client Name">{{ props.item.booking_done_by }}</td>
        <td data-label="Entry Pax">{{ props.item.guest_total }}</td>
        <td data-label="Payment Status"><b>{{ props.item.booking_payment_status }}</b></td>
        <v-label><td data-label="Status" v-bind:style="props.item.booking_color"> {{ props.item.booking_check_in_status }}</td></v-label>
        <td data-label="Action">
          <v-btn :depressed="true"
                 color="#587e27"
                 small
                 @click="checkIn(props.item.booking_public_id)"
                 v-if="props.item.booking_check_in_status === 'Checking In'"
                 class="futuristic-btn">Check In</v-btn>
        </td>
      </template>
      <v-alert slot="no-results" :value="true" color="error" icon="warning" class="futuristic-alert">
        Your search for "{{ search }}" found no results.
      </v-alert>
      <v-alert slot="no-data" :value="true" color="info" icon="info" class="futuristic-alert">
        No check‑in bookings found for this type.
      </v-alert>
    </v-data-table>
  </v-card>
</template>

<script>
  export default {
    name: 'check-in-today',
    data() {
      return {
        search: '',
        selectedTypeTab: 0, // 0 -> "All" by default
        columns: [
          {
            text: 'Ref',
            value: 'booking_ref_code'
          },
          {
            text: 'Vehicle Registration',
            value: 'vehicle_registration',
          },
          {
            text: 'Booking Type',
            value: 'booking_type'
          },
          {
            text: 'Client Name',
            value: 'booking_done_by'
          },
          {
            text: 'Entry Pax',
            value: 'guest_total'
          },
          {
            text: 'Payment Status',
            value: 'booking_payment_status'
          },
          {
            text: 'Status',
            value: 'booking_check_in_status'
          },
          {
            text: 'Action',
            value: ''
          }
        ]
      }
    },
    computed: {
      // Build list of tabs: first "All", then each unique booking type
      bookingTypes() {
        const typesSet = new Set()
        this.getCheckInToday.forEach(b => {
          if (b.booking_type) {
            const t = String(b.booking_type).trim()
            if (t) typesSet.add(t)
          }
        })
        const sorted = Array.from(typesSet).sort()
        // Prepend "All"
        const all = { label: 'All', value: null } // null means no filter
        return [all, ...sorted.map(t => ({
          label: this.formatTypeLabel(t),
          value: t
        }))]
      },

      // Get the currently selected type value (null for "All")
      selectedType() {
        if (this.bookingTypes.length === 0) return null
        const index = this.selectedTypeTab
        return this.bookingTypes[index] ? this.bookingTypes[index].value : null
      },

      // Filter items: if selectedType is null (All) return all, else filter by type
      filteredItems() {
        let items = this.getCheckInToday
        if (this.selectedType !== null) {
          items = items.filter(b => String(b.booking_type).trim() === this.selectedType)
        }
        return items
      }
    },
    watch: {
      getCheckInToday: {
        handler() {
          // Reset tab to 0 (All) if current tab index is out of bounds
          if (this.selectedTypeTab >= this.bookingTypes.length) {
            this.selectedTypeTab = 0
          }
        },
        immediate: true
      }
    },
    created() {
      this.$store.dispatch('get_check_in_bookings_today')
    },
    methods: {
      checkIn(publicId) {
        this.$router.push({
          name: 'Checkin',
          params: {
            booking_id: publicId
          }
        })
      },

      formatTypeLabel(type) {
        return String(type)
          .toLowerCase()
          .split(' ')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ')
      },

      getTypeCount(type) {
        // If type is null (All), return total count
        if (type === null) {
          return this.getCheckInToday.length
        }
        return this.getCheckInToday.filter(b => String(b.booking_type).trim() === type).length
      }
    }
  }
</script>

<style scoped>
  /* Futuristic Card */
  .futuristic-card {
    border-radius: 8px;
    background: #ffffff;
    transition: all 0.3s ease;
  }

    .futuristic-card:hover {
      box-shadow: 0 6px 20px rgba(0, 0, 0, 0.1);
    }

  /* Card Header */
  .card-header {
    background: linear-gradient(45deg, #e8eaf6, #f5f7fa);
    padding: 16px;
    border-radius: 8px;
  }

  .earth-color {
    color: #587e27;
    font-weight: 600;
    margin: 0;
    font-size: 1.5rem;
  }

  .title-decoration {
    height: 4px;
    background: linear-gradient(90deg, #587e27, transparent);
    margin-bottom: 16px;
    width: 100px;
  }

  /* Input Fields */
  .futuristic-search > > > .v-input__slot {
    border-radius: 8px;
    background: #f5f7fa;
  }

  /* Buttons */
  .futuristic-btn {
    border-radius: 8px;
    text-transform: none;
    font-weight: 500;
    transition: all 0.2s ease;
  }

    .futuristic-btn:hover {
      transform: translateY(-1px);
      box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    }

  /* Data Table */
  .futuristic-table {
    border-radius: 8px;
    background: #ffffff;
  }

    .futuristic-table > > > th {
      color: #587e27;
      font-weight: 600;
    }

  /* Links */
  .futuristic-link {
    color: #587e27;
    text-decoration: none;
    transition: color 0.2s ease;
  }

    .futuristic-link:hover {
      color: #1b5e20;
    }

  /* Alerts */
  .futuristic-alert {
    border-radius: 8px;
    background: #fff5f5;
  }

  /* Booking type tabs */
  .type-tabs .v-tabs__bar {
    background: linear-gradient(45deg, #f5f7fa, #e8eaf6);
    margin: 0 8px;
    border-radius: 8px 8px 0 0;
  }

  .type-tabs .v-tabs__item {
    font-size: 0.85rem;
    font-weight: 500;
    color: #587e27;
    text-transform: none;
  }

  .type-tabs .v-tabs__item--active {
    background: rgba(88, 126, 39, 0.1);
  }

  /* Responsive Adjustments */
  @media (max-width: 960px) {
    .card-header {
      padding: 12px;
    }

    .earth-color {
      font-size: 1.25rem;
    }

    .futuristic-btn {
      font-size: 0.9rem;
    }

    .type-tabs .v-tabs__item {
      font-size: 0.8rem;
      padding: 0 8px;
    }
  }

  @media (max-width: 600px) {
    .card-header {
      padding: 8px;
    }

    .earth-color {
      font-size: 1.1rem;
    }

    .futuristic-btn {
      font-size: 0.85rem;
    }

    .type-tabs .v-tabs__wrapper {
      overflow-x: auto;
    }

    .type-tabs .v-tabs__container {
      min-width: 500px;
    }

    .type-tabs .v-tabs__item {
      font-size: 0.75rem;
      padding: 0 6px;
      min-width: auto;
    }

    .futuristic-table table {
      min-width: 700px;
    }
  }
</style>
